/**
 * "We couldn't find your booking" — who sees it and what it says (force-v2 Part 6).
 *
 * Every client-side role (client, collaborator, planner) reaches the v2 portal; before this only
 * role 'client' got the screen, so a collaborator whose invitation was revoked (or a planner not yet
 * assigned) landed in an empty portal. Copy fits the role: a couple is told to get their booking
 * linked, a collaborator to ask the couple for an invitation, a planner to get assigned.
 * Pure, so App.tsx and the test share it.
 */
import { isClientSideRole } from "./flag";

export interface OnboardingInput {
  role?: string | null;
  id?: number | null;
  isTransportCustomer?: boolean | null;
  weddingsLoading: boolean;
  weddingsError: boolean;
  weddings: unknown;
}

/** Signed-in client-side user whose (settled, successful) weddings list is empty. */
export function needsOnboardingFor(i: OnboardingInput): boolean {
  if (!isClientSideRole(i.role) || !i.id) return false;
  // Van-rental OTP accounts are role 'client' with no wedding by design (2026-08-09).
  if (i.role === "client" && i.isTransportCustomer) return false;
  if (i.weddingsLoading || i.weddingsError) return false;
  return Array.isArray(i.weddings) && i.weddings.length === 0;
}

export interface NoBookingCopy { title: string; body: string }

export function noBookingCopy(role: string | null | undefined): NoBookingCopy {
  if (role === "collaborator") {
    return {
      title: "No wedding shared with you yet",
      body: "Your account is not linked to a wedding at LA Mirage yet. Ask the couple to invite you from their Family hub and open the link on this phone. If they already have, contact us and we will sort it out.",
    };
  }
  if (role === "planner") {
    return {
      title: "No wedding assigned to you yet",
      body: "Your planner account is not assigned to a wedding at LA Mirage yet. Contact us with the couple's names and wedding date and we will assign it to you.",
    };
  }
  return {
    title: "We couldn't find your booking",
    body: "Your account is not linked to a wedding at LA Mirage yet. If you have booked with us, please contact us and we will link your booking to this account.",
  };
}
